import { useEffect, useState } from 'react'
import { getImagePermission } from '../services/das/dasService'
import { imageErrorStates, getImageErrorState } from '../utils/imageErrorStates'

/**
 * @typedef {Object} UseImagePermissionResult
 * @property {string|null} imagePermission - The DAS permissions for the image, a colon-delimited
 *   string, e.g. 'ThemisPrmAnyone:ThemisPrmNotSignedIn'.
 * @property {string|null} imageErrorState - An `imageErrorStates` value when the image can't be shown, else null.
 */

/**
 * Fetches the DAS permissions for an image.
 *
 * @param {Object} params
 * @param {string} params.imageArk - The image ark, e.g. '3:1:33S7-9GYC-S4DY'.
 * @param {boolean} params.enabled - Whether to make the call; skip it on non-ark routes.
 * @returns {UseImagePermissionResult}
 */
export default function useImagePermission({ imageArk, enabled }) {
  const [imagePermission, setImagePermission] = useState(null)
  const [imageErrorState, setImageErrorState] = useState(null)

  useEffect(() => {
    const updateImagePermission = async () => {
      setImageErrorState(null)
      try {
        const response = await getImagePermission(imageArk)
        if (!response.data) {
          // DAS answered but gave us no permissions, so we can't tell whether the image is viewable.
          setImagePermission(null)
          setImageErrorState(imageErrorStates.UNAVAILABLE)
          return
        }
        setImagePermission(response.data)
      } catch (error) {
        setImagePermission(null)
        // No status means the request never reached DAS, which is a transient problem rather than a
        // missing image.
        setImageErrorState(getImageErrorState(error?.response?.status) ?? imageErrorStates.UNAVAILABLE)
      }
    }
    if (enabled) {
      updateImagePermission()
    } else {
      // RootBase stays mounted across routes, so without this the last image's permission/error would
      // linger on non-ark routes.
      setImagePermission(null)
      setImageErrorState(null)
    }
  }, [imageArk, enabled])

  return { imagePermission, imageErrorState }
}
